import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listDelegations, createDelegation, revokeDelegation,
  DELEGATION_SCOPES, type Delegation, type WorkflowInstance,
} from '../../api/workflowApi.js';
import { sx, Field, SelectField, toOptions, todayIso } from './wfKit.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { UserPicker } from '../../components/UserPicker.js';
import { RecordPicker } from '../../components/RecordPicker.js';

/**
 * WorkflowDelegationsPage — standalone delegation management (T054).
 *
 * Lists the tenant's delegations and lets an admin create a new delegation
 * (delegator = the acting user from the JWT) or revoke an existing one, via
 * `/v1/wf/delegations`.
 */
export function WorkflowDelegationsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // effective_from / effective_to are timestamptz (t054_workflow_delegation),
  // so slicing the first ten characters printed the UTC day: after 18:30 IST
  // a delegation starting today was shown as starting yesterday. `atDay`
  // reads the instant on the reader's own clock and writes it in the tenant's
  // DISPLAY_DATE_FORMAT.
  const { atDay } = useBusinessDay();
  const [rows, setRows] = useState<Delegation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [delegatee, setDelegatee] = useState('');
  const [scope, setScope] = useState<string>('GLOBAL');
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState('');
  const [instanceId, setInstanceId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    listDelegations(token).then(setRows).catch((e) => setError((e as Error).message));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  async function create(): Promise<void> {
    if (!delegatee) { setError(t('wf.delegations.delegateeReq')); return; }
    if (!to) { setError(t('wf.delegations.toReq')); return; }
    if (scope === 'INSTANCE' && !instanceId) { setError(t('wf.delegations.instanceReq')); return; }
    setError(null); setNotice(null); setBusy(true);
    try {
      await createDelegation(token, {
        delegateeUserId: delegatee,
        effectiveFrom: from,
        effectiveTo: to,
        scope: scope as Delegation['delegationScope'],
        instanceId: scope === 'INSTANCE' ? instanceId : undefined,
        reason: reason.trim() || undefined,
      });
      setDelegatee(''); setInstanceId(''); setReason(''); setTo('');
      setNotice(t('wf.delegations.created'));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string): Promise<void> {
    setError(null); setNotice(null);
    try {
      await revokeDelegation(token, id);
      setNotice(t('wf.delegations.revoked'));
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <main aria-label="wf-delegations" style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{t('wf.delegations.title')}</h1>
        <Link to="/admin/workflows" style={sx.secondaryBtn}>{t('wf.common.designer')}</Link>
      </div>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      <section aria-label="new-delegation" style={{ ...sx.card }}>
        <h2 style={{ fontSize: 15, marginTop: 0 }}>{t('wf.delegations.newDelegation')}</h2>
        <UserPicker label={t('wf.delegations.delegateeLabel')} testId="del-delegatee" value={delegatee} onPick={(id) => setDelegatee(id)} />
        <SelectField label={t('wf.delegations.scope')} name="del-scope" value={scope} onChange={setScope} options={toOptions(DELEGATION_SCOPES)} />
        {scope === 'INSTANCE' && (
          <RecordPicker<WorkflowInstance>
            endpoint="/v1/wf/instances?limit=200"
            idField="instanceId"
            label={t('wf.delegations.instanceLabel')}
            testId="del-instance"
            value={instanceId}
            optionLabel={(i) => [i.instanceRefNumber, i.entityRefNumber, i.instanceStatus].filter(Boolean).join(' · ')}
            onPick={(id) => setInstanceId(id)}
          />
        )}
        <Field label={t('wf.common.effectiveFrom')} name="del-from" value={from} onChange={setFrom} type="date" required />
        <Field label={t('wf.common.effectiveTo')} name="del-to" value={to} onChange={setTo} type="date" required />
        <Field label={t('wf.delegations.reason')} name="del-reason" value={reason} onChange={setReason} />
        <div style={sx.actionBar}>
          <Button type="button" variant="primary" disabled={busy} aria-label="create-delegation" onClick={create}>
            {busy ? t('wf.delegations.creating') : t('wf.delegations.createBtn')}
          </Button>
        </div>
      </section>

      {!rows ? <p>{t('common.loading')}</p> : rows.length === 0 ? (
        <p data-testid="delegations-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.delegations.empty')}</p>
      ) : (
        <DataTable<Delegation>
          ariaLabel="delegations-table"
          rows={rows}
          rowKey={(d) => d.delegationId}
          rowTestId={(d) => `delegation-${d.delegationId}`}
          columns={[
            { key: 'delegatee', header: t('wf.delegations.colDelegatee'), render: (d) => personLabel(d.actorName ?? null, d.actorCode ?? null) ?? '—' },
            { key: 'scope', header: t('wf.delegations.scope'), render: (d) => d.delegationScope },
            { key: 'from', header: t('wf.delegations.colFrom'), render: (d) => atDay(d.effectiveFrom) },
            { key: 'to', header: t('wf.delegations.colTo'), render: (d) => atDay(d.effectiveTo) },
            { key: 'status', header: t('wf.common.status'), render: (d) => d.delegationStatus },
            { key: 'actions', header: '', render: (d) => (
              d.delegationStatus === 'ACTIVE' ? (
                <Button type="button" variant="danger"
                  aria-label={`revoke-${d.delegationId}`} onClick={() => revoke(d.delegationId)}>{t('wf.delegations.revoke')}</Button>
              ) : null
            ) },
          ]}
        />
      )}
    </main>
  );
}
